// The guard layer: the round kill and the cooldown marks of every guard
// (R2080, `ui-map-interface.md` §6.3.6, mockup round 099 card 04 with the
// snap of card 01).
//
// A guard is any object that takes one hostile round out of the water on a
// cooldown (numbers.md §5). This layer knows no host type: the scene hands
// it a kill as a point pair, and the guard records come off the clocks of
// `state.guards` with a point the feed answers for each host. The look is
// `render/guard-look.ts`, a leaf; this layer owns the clock of the kills,
// the batches and the cull, and fills ONE reused record a mark (R131).
//
// **Who sees what.** Every client that receives a ROUND_KILL draws the kill,
// because the kill itself is no secret: it reached the client through the
// viewport gate of every event. The cooldown marks draw for a guard whose
// clock this client holds and for no other. The server sends a clock to
// the guard's owner (and an arcade team), never to a hostile client, so a
// hostile guard has no record here and the layer guesses none.
//
// It draws through the segment, the circle and the quad batches of the
// engine, so the WebGL2 and the WebGPU backends draw it alike, and it adds
// no program. The digit of the pip rides the chart's mono cells (R2418). The slot `guard` stands right after `fx`, so a kill draws over
// the round marks and the blasts and under the gun shots.

import { factionColor } from '../../world';
import {
  KILL_LIFE_SEC, guardPhase, packCool, packKill,
  type GuardKill, type GuardMarks, type GuardRound, type GuardSinks,
} from '../../render/guard-look';
import { GUARD } from '../../gen/presentation';
import type { GuardClock, GuardClocks } from '../../guards';
import { F_ROUND, type DrawCtx, type FrameInfo, type Layer } from '../layer';
import type { BatchSource, CircleSink, QuadSink, SegmentSink } from './batches';
import { ChartGlyphs } from '../chart/glyphs';
import { zoomOf } from './shared';
import type { RoundSnap } from './torpedoes';

/** Where the layer reads the world. */
export interface GuardFeeds {
  /** The rounds in flight, as the torpedo layer draws them now. */
  rounds(): readonly RoundSnap[];
  /** The guard clocks this client holds. */
  clocks(): GuardClocks;
  /** Where a guard stands now, or null when the client cannot place it. */
  at(g: GuardClock): { x: number; y: number } | null;
  /** Where a guard's pip stands against its host's point, into `out`. */
  pip(g: GuardClock, out: GuardPip): void;
  /** World seconds for map seconds, the figure the card prints. */
  worldSec(mapSec: number): number;
}

/** The place of a pip against its host's point, and its radius, world px. */
export interface GuardPip {
  dx: number;
  dy: number;
  r: number;
}

/** How many instances each sink holds before the engine grows it. */
const SEGMENT_CAPACITY = 128;
const CIRCLE_CAPACITY = 64;
const QUAD_CAPACITY = 32;

/** One live kill and its age. The records are pooled (R131). */
interface Kill extends GuardKill {
  live: boolean;
}

export class GuardLayer implements Layer {
  readonly name = 'guard';
  private shown = true;
  private readonly segs: SegmentSink;
  private readonly circles: CircleSink;
  private readonly quads: QuadSink;
  private readonly glyphs: ChartGlyphs;
  private readonly sinks: GuardSinks;
  private readonly kills: Kill[] = [];
  /** The one record every guard's marks reuse, and its near list. */
  private readonly marks: GuardMarks;
  private readonly near: GuardRound[] = [];
  private readonly pip: GuardPip = { dx: 0, dy: 0, r: 0 };
  /** The pass over one guard, bound once, and what it reads this frame,
   *  so a frame allocates no closure (R131). */
  private readonly markOne = (g: GuardClock): void => this.packGuard(g);
  private frameRounds: readonly RoundSnap[] = [];
  private frameNow = 0;

  constructor(src: BatchSource, private readonly feeds: GuardFeeds) {
    this.segs = src.newSegments(SEGMENT_CAPACITY);
    this.circles = src.newCircles(CIRCLE_CAPACITY);
    this.quads = src.newQuads(QUAD_CAPACITY);
    this.glyphs = new ChartGlyphs(src);
    this.sinks = {
      segs: this.segs, circles: this.circles, zoom: 1, round: F_ROUND,
      text: (t, x, y, px, ink, alpha) => {
        this.glyphs.run(this.quads, t, x, y, 'mono', px, ink, alpha, { anchor: 'centre', halo: true });
      },
    };
    this.marks = {
      x: 0, y: 0, px: 0, py: 0, r: 0, reach: 0, frac: 0, left: 0, phase: 'idle', ink: 0,
      next: null, nextD: 0, near: this.near,
    };
  }

  /**
   * One round taken out of the water (EVENT_KIND_ROUND_KILL): the killer's
   * point, the round's point and heading, the killer's faction and the
   * round's. The age counts wall seconds from now.
   */
  kill(fromX: number, fromY: number, x: number, y: number, heading: number,
    faction: number, roundFaction: number): void {
    let k = this.kills.find((r) => !r.live);
    if (!k) {
      k = { fromX: 0, fromY: 0, x: 0, y: 0, heading: 0, ink: 0, roundInk: 0, age: 0, live: false };
      this.kills.push(k);
    }
    k.fromX = fromX; k.fromY = fromY; k.x = x; k.y = y; k.heading = heading;
    // A public structure's faction is 0, and factionColor(0) is the neutral grey.
    k.ink = factionColor(faction); k.roundInk = factionColor(roundFaction);
    k.age = 0; k.live = true;
  }

  /** How many kills draw now. The tests read it. */
  get liveKills(): number { return this.kills.reduce((n, k) => n + (k.live ? 1 : 0), 0); }

  /** Takes every kill off the water now: a film that seeks, a new hello. */
  clear(): void { for (const k of this.kills) k.live = false; }

  update(frame: FrameInfo): void {
    const dt = frame.dtMs / 1000;
    for (const k of this.kills) {
      if (!k.live) continue;
      k.age += dt;
      if (k.age >= KILL_LIFE_SEC) k.live = false;
    }
    this.sinks.zoom = zoomOf(frame.cam.zoom);
    this.segs.begin();
    this.circles.begin();
    this.quads.begin();
    this.packMarks(Date.now());
    for (const k of this.kills) if (k.live) packKill(this.sinks, k);
  }

  /**
   * The cooldown marks of every guard whose clock this client holds. Each
   * guard reads the rounds of another side inside its reach (the near
   * list) and the nearest one inside `watch_reaches` reaches; a public
   * guard (faction 0) reads every round. The phase and the count go back
   * onto the clock record for the readout chip of the card.
   */
  private packMarks(nowMs: number): void {
    const clocks = this.feeds.clocks();
    if (clocks.size === 0) return;
    this.frameRounds = this.feeds.rounds();
    this.frameNow = nowMs;
    clocks.forEach(this.markOne);
    this.frameRounds = [];
  }

  /** The marks of one guard, with the rounds and the time of the frame. */
  private packGuard(g: GuardClock): void {
    const at = this.feeds.at(g);
    if (!at) return;
    const m = this.marks, near = this.near;
    near.length = 0;
    let next: RoundSnap | null = null;
    let nextD = Infinity;
    const watch = g.reach * GUARD.watchReaches;
    for (const r of this.frameRounds) {
      if (g.faction !== 0 && r.faction === g.faction) continue;
      const d = Math.hypot(r.x - at.x, r.y - at.y);
      if (d <= g.reach) near.push(r);
      if (d <= watch && d < nextD) { next = r; nextD = d; }
    }
    const clocks = this.feeds.clocks();
    const frac = clocks.frac(g, this.frameNow);
    g.phase = guardPhase(frac, near.length, next !== null);
    g.near = near.length;
    const pip = this.pip;
    this.feeds.pip(g, pip);
    m.x = at.x; m.y = at.y; m.px = at.x + pip.dx; m.py = at.y + pip.dy; m.r = pip.r;
    m.reach = g.reach;
    m.frac = frac; m.phase = g.phase; m.ink = factionColor(g.faction);
    m.left = frac > 0 ? Math.ceil(this.feeds.worldSec(clocks.leftSec(g, this.frameNow))) : 0;
    m.next = next; m.nextD = nextD;
    packCool(this.sinks, m);
  }

  draw(ctx: DrawCtx): void {
    if (!this.shown) return;
    this.segs.flush(ctx.cam);
    this.circles.flush(ctx.cam);
    this.quads.flush(ctx.cam);
  }

  setVisible(v: boolean): void { this.shown = v; }
  get visible(): boolean { return this.shown; }
}
